import React, { Fragment } from 'react';
import {
  TableRow,
  TableCell,
  IconButton,
  Collapse,
  Box,
  Checkbox,
  Table,
  TableBody,
  Typography
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { ShopifyInventoryItem, ShopifyVariant } from '../../../graphql/generated';

interface ProductRowProps {
  product: ShopifyInventoryItem;
  expanded: { [key: string]: boolean };
  handleExpandClick: (productId: string) => void;
  handleSelectProduct: (productId: string, variants: ShopifyVariant[]) => void;
  selected: { [key: string]: boolean };
}

const ProductRow: React.FC<ProductRowProps> = ({
  product,
  expanded,
  handleExpandClick,
  handleSelectProduct,
  selected,
}) => {
  const totalVariants = product.variants.length;
  const productImage = product.image ? product.image.src : '';
const prices = product.variants
  .map(variant => variant.price)
  .filter(price => price !== null) as number[];

const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

const variantGroups = product.variants.reduce((groups: any, variant: ShopifyVariant) => {
  const option1Key = variant.option1 || 'Unknown Option';
  
  if (!groups[option1Key]) {
    groups[option1Key] = [];
  }
  
  groups[option1Key].push(variant);
  
  return groups;
}, {});


  const getPriceRange = (variants: ShopifyVariant[]) => {
    const variantPrices = variants
  .map(variant => variant.price)
  .filter(price => price !== null) as number[];

const minVariantPrice = prices.length > 0 ? Math.min(...variantPrices) : 0;
const maxVariantPrice = prices.length > 0 ? Math.max(...variantPrices) : 0;
    return minVariantPrice !== maxVariantPrice ? `${minVariantPrice} - ${maxVariantPrice}` : minVariantPrice;
  };

  const getTotalQuantity = (variants: ShopifyVariant[]) => {
    return variants.reduce((sum, variant) => sum + (variant.inventoryQuantity ?? 0), 0);
  };

  const getSecondaryOptionName = () => {
    const secondaryOption = product.options.find(option => option.name !== product.options[0].name);
    return secondaryOption ? secondaryOption.name : 'options';
  };

  const getTertiaryOptionName = () => {
    const tertiaryOption = product.options.find(
      option => option.name !== product.options[0].name && option.name !== getSecondaryOptionName()
    );
    return tertiaryOption ? tertiaryOption.name : null;
  };

  const pluralize = (word: string, count: number) => {
    return count > 1 ? `${word}s` : word;
  };

  const getVariantDescription = (option1Value: string) => {
    const secondaryOptionName = getSecondaryOptionName();
    const tertiaryOptionName = getTertiaryOptionName();

    const secondaryOptionCount = new Set(
      variantGroups[option1Value].map((variant: ShopifyVariant) => variant.option2)
    ).size;

    const tertiaryOptionCount = new Set(
      variantGroups[option1Value].map((variant: ShopifyVariant) => variant.option3)
    ).size;

    let description = '';
    if (secondaryOptionCount > 1) {
      description += `${secondaryOptionCount} ${pluralize(secondaryOptionName, secondaryOptionCount)}`;
    }
    if (tertiaryOptionCount > 1) {
      if (description) description += ', ';
      description += `${tertiaryOptionCount} ${pluralize(tertiaryOptionName!, tertiaryOptionCount)}`;
    }
    return description ? `(${description})` : '';
  };

  return (
    <Fragment>
      <TableRow>
        <TableCell>
          <Checkbox
            checked={selected[product.id] || false}
            onChange={() => handleSelectProduct(product.id, product.variants)}
          />
        </TableCell>
        <TableCell>
          {productImage && (
            <img
              src={productImage}
              alt={product.image?.altText || "Product image"}
              height="50"
              style={{ maxWidth: 50 }}
            />
          )}
        </TableCell>
        <TableCell>
          <IconButton onClick={() => handleExpandClick(product.id)}>
            {expanded[product.id] ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          {product.title}
          {totalVariants > 1 && (
            <Typography variant="body2">
              {totalVariants} variants
            </Typography>
          )}
        </TableCell>
        <TableCell>
          {product.variants[0].sku || ''}
        </TableCell>
        <TableCell>{minPrice !== maxPrice ? `${minPrice} - ${maxPrice}` : minPrice}</TableCell>
        <TableCell>{getTotalQuantity(product.variants)}</TableCell>
        <TableCell>
          {product.variants[0].inventoryItemId || ''}
        </TableCell>
        <TableCell>
          {product.id}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={expanded[product.id]} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table size="small" aria-label="variant-groups">
                <TableBody>
                  {Object.keys(variantGroups).map(option1Value => (
                    <TableRow key={option1Value}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {option1Value} {getVariantDescription(option1Value)}
                        </Typography>
                      </TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell>{getPriceRange(variantGroups[option1Value])}</TableCell>
                      <TableCell>{getTotalQuantity(variantGroups[option1Value])}</TableCell>
                      <TableCell />
                      <TableCell />
                      <TableCell />
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};

export default ProductRow;
